/**
 * Draft handoff between the `client_create_skill` client tool and the
 * Skills page's create-skill editor.
 *
 * The tool stashes a JSON draft under the storage key below and
 * navigates the user to ``/agents/skills``. The Skills page consumes the
 * draft once on mount, opens the create editor seeded with it, and clears
 * the slot — so refreshing the page or opening the Skills page from another
 * entry point produces the usual empty list (and an empty create form).
 *
 * The shape mirrors the editor's create-form fields rather than the wire
 * `CreateSkillRequest`: every field is optional so the LLM can emit what it
 * inferred and let the user fill or fix the rest in the editor, which owns
 * the final validation before the skill is persisted.
 *
 * SessionStorage is a trust boundary: values can be tampered with via
 * DevTools, browser extensions, or left behind by an older build with a
 * different schema. The consume path runs a runtime shape validator so the
 * editor's lazy state initializer never sees a malformed payload.
 */

import type { SkillAccessibility } from "./SkillRecord";

const SKILL_DRAFT_STORAGE_KEY = "roboto.skill-draft";

const ACCESSIBILITIES: readonly SkillAccessibility[] = [
  "private",
  "org",
  "org-editable",
];

/**
 * Partial skill specification handed from the client tool to the create
 * editor. Every field is optional; the editor maps missing fields back to
 * its own empty-form defaults and surfaces any remaining gaps via its own
 * validation.
 */
export interface SkillDraft {
  /** Slug-style name. May be invalid (e.g. contain spaces) — the editor's
   *  name field validates it and prompts the user to fix it. */
  name?: string;
  /** The "when to use" text. */
  description?: string;
  /** The procedure / body (Markdown). */
  body?: string;
  /** Visibility tier. Omitted leaves the editor on its `private` default. */
  accessibility?: SkillAccessibility;
  tags?: string[];
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function asString(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

/**
 * Coerce arbitrary parsed JSON into a {@link SkillDraft}.
 *
 * Returns `null` if the value isn't a plain object. Otherwise returns a
 * draft that contains *only* fields whose runtime shape matches the type —
 * wrong-typed scalars are dropped, a non-array or partially non-string
 * `tags` collapses to the string entries it does carry. A partially-corrupt
 * stored draft yields a partially-populated form rather than a crash.
 *
 * Exposed for tests; callers should use {@link consumeSkillDraft}.
 */
export function parseSkillDraftShape(value: unknown): SkillDraft | null {
  if (!isPlainObject(value)) {
    return null;
  }
  const draft: SkillDraft = {};
  const name = asString(value.name);
  if (name !== undefined) {
    draft.name = name;
  }
  const description = asString(value.description);
  if (description !== undefined) {
    draft.description = description;
  }
  const body = asString(value.body);
  if (body !== undefined) {
    draft.body = body;
  }
  // Only a recognized tier survives — an unknown / wrong-typed accessibility
  // is dropped so the editor falls back to its `private` default rather than
  // rendering a Select with an out-of-range value.
  if (ACCESSIBILITIES.includes(value.accessibility as SkillAccessibility)) {
    draft.accessibility = value.accessibility as SkillAccessibility;
  }
  if (Array.isArray(value.tags)) {
    const tags: string[] = [];
    for (const tag of value.tags) {
      if (typeof tag === "string") {
        tags.push(tag);
      }
    }
    if (tags.length > 0) {
      draft.tags = tags;
    }
  }
  return draft;
}

// ---------------------------------------------------------------------------
// Consume cache
//
// React 18 StrictMode (dev) mounts → unmounts → remounts every component on
// initial render. The consume side effect ("read sessionStorage then remove
// the slot") is not idempotent across that cycle: the first mount reads the
// draft and clears the slot; the synthetic remount would then see an empty
// slot and return `null`. The cache below preserves the consume result across
// one short window (current task + queued macrotask) so the synthetic remount
// returns the same draft. A later true visit (navigate away and back) sees the
// cache cleared and reads from sessionStorage normally. `stashSkillDraft` also
// invalidates the cache so a new write supersedes an older cached read. This
// mirrors the agent-draft handoff — see `shared/domain/agents/agentDraft.ts`.
// ---------------------------------------------------------------------------

interface ConsumeCache {
  value: SkillDraft | null;
}

let consumeCache: ConsumeCache | undefined = undefined;
let consumeCacheClearScheduled = false;

function scheduleConsumeCacheClear(): void {
  if (consumeCacheClearScheduled) {
    return;
  }
  consumeCacheClearScheduled = true;
  // setTimeout(0) fires after the current task and any microtasks it queues.
  // React's StrictMode synthetic remount completes within that same task, so
  // the second mount sees the cache; visits that happen later start a new task
  // and find the cache cleared.
  setTimeout(() => {
    consumeCache = undefined;
    consumeCacheClearScheduled = false;
  }, 0);
}

// ---------------------------------------------------------------------------
// Storage helpers
// ---------------------------------------------------------------------------

/**
 * Write the draft to sessionStorage. Silently no-ops if storage is
 * unavailable (e.g. private-mode quirks); the Skills page handles the
 * resulting absent draft as if the user had opened the page directly.
 */
export function stashSkillDraft(draft: SkillDraft): void {
  try {
    sessionStorage.setItem(SKILL_DRAFT_STORAGE_KEY, JSON.stringify(draft));
  } catch {
    // Storage unavailable — fall back to the empty create form.
  }
  // Invalidate any cached read so the next `consumeSkillDraft` sees this
  // stash, not the prior visit's value.
  consumeCache = undefined;
}

/**
 * Read, validate, and clear the pending draft.
 *
 * Returns `null` when no draft is pending, when the stored payload fails to
 * parse, or when the parsed value isn't a plain object. Any nested field
 * whose runtime shape doesn't match its declared type is dropped silently via
 * {@link parseSkillDraftShape}.
 *
 * The slot is cleared even on parse failure so a corrupt entry can't wedge
 * subsequent visits. The result is briefly cached at the module level — see
 * the comment block above — so a React 18 StrictMode synthetic remount sees
 * the same draft as the first mount rather than an empty slot.
 */
export function consumeSkillDraft(): SkillDraft | null {
  if (consumeCache !== undefined) {
    return consumeCache.value;
  }
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(SKILL_DRAFT_STORAGE_KEY);
  } catch {
    return null;
  }
  let parsed: SkillDraft | null;
  if (raw === null) {
    parsed = null;
  } else {
    try {
      sessionStorage.removeItem(SKILL_DRAFT_STORAGE_KEY);
    } catch {
      // Best-effort clear.
    }
    try {
      parsed = parseSkillDraftShape(JSON.parse(raw));
    } catch {
      parsed = null;
    }
  }
  consumeCache = { value: parsed };
  scheduleConsumeCacheClear();
  return parsed;
}

/**
 * Internal hook for tests — exported so tests can reset state between cases
 * without poking at the storage key directly.
 */
export const __SKILL_DRAFT_STORAGE_KEY_FOR_TESTS: string =
  SKILL_DRAFT_STORAGE_KEY;

/**
 * Internal hook for tests — synchronously clear the module-level consume cache
 * so each test starts from a clean state. Tests normally let the
 * setTimeout-based clear handle this, but a tight sequence of stash+consume
 * cycles in one test needs an explicit reset to avoid the previous test's
 * cache bleeding through.
 */
export function __resetSkillDraftCacheForTests(): void {
  consumeCache = undefined;
  consumeCacheClearScheduled = false;
}
